'use client';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import MWSVG from '@/components/common/svg';
import Image from 'next/image';
import createGlobe, { COBEOptions } from 'cobe';

const getGlobeConfig = (isDark: boolean): COBEOptions => ({
    width: isDark ? 600 * 2 : 800,
    height: isDark ? 600 * 2 : 800,
    onRender: () => {},
    devicePixelRatio: 2,
    phi: 0,
    theta: isDark ? 0 : 0.3,
    dark: isDark ? 1 : 0,
    diffuse: isDark ? 1.2 : 0,
    mapSamples: 16000,
    mapBrightness: isDark ? 6 : 1.5,
    baseColor: isDark ? [0.3, 0.3, 0.3] : [1, 1, 1],
    markerColor: isDark ? [0.1, 0.8, 1] : [5 / 255, 166 / 255, 240 / 255],
    glowColor: isDark ? [0.5, 0.5, 0.5] : [1, 1, 1],
    markers: [
        { location: [14.5995, 120.9842], size: 0.03 },
        { location: [19.076, 72.8777], size: 0.1 },
        { location: [23.8103, 90.4125], size: 0.05 },
        { location: [30.0444, 31.2357], size: 0.07 },
        { location: [39.9042, 116.4074], size: 0.08 },
        { location: [-23.5505, -46.6333], size: 0.1 },
        { location: [19.4326, -99.1332], size: 0.1 },
        { location: [40.7128, -74.006], size: 0.1 },
        { location: [34.6937, 135.5022], size: 0.05 },
        { location: [41.0082, 28.9784], size: 0.06 },
    ],
});

function Globe() {
    let phi = 0;
    let width = 0;
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const globeRef = useRef<any>(null);
    const [isVisible, setIsVisible] = useState(false);
    const [isDark, setIsDark] = useState(false);
    const [mounted, setMounted] = useState(false);

    useEffect(() => {
        setMounted(true);
    }, []);

    // Detect theme changes
    useEffect(() => {
        if (!mounted) return;
        
        const checkTheme = () => {
            setIsDark(document.documentElement.classList.contains('dark'));
        };

        checkTheme();

        // Watch for theme changes
        const observer = new MutationObserver(checkTheme);
        observer.observe(document.documentElement, {
            attributes: true,
            attributeFilter: ['class'],
        });

        return () => observer.disconnect();
    }, []);

    const onRender = useCallback((state: Record<string, any>) => {
        // Simple auto-rotation without user interaction
        phi += 0.005;
        state.phi = phi;
        state.width = width * 2;
        state.height = width * 2;
    }, []);

    const onResize = useCallback(() => {
        if (canvasRef.current) {
            width = canvasRef.current.offsetWidth;
        }
    }, []);

    // Use Intersection Observer to only render when visible
    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => {
                setIsVisible(entry.isIntersecting);
            },
            { threshold: 0.1 },
        );

        if (canvasRef.current) {
            observer.observe(canvasRef.current);
        }

        return () => {
            if (canvasRef.current) {
                observer.unobserve(canvasRef.current);
            }
        };
    }, []);

    useEffect(() => {
        if (!mounted) return;
        
        window.addEventListener('resize', onResize);
        onResize();

        // Destroy existing globe if theme changed
        if (globeRef.current) {
            globeRef.current.destroy();
            globeRef.current = null;
        }

        // Only create the globe if the component is visible
        if (isVisible && canvasRef.current) {
            const config = getGlobeConfig(isDark);
            const optimizedConfig = {
                ...config,
                width: width * 2,
                height: width * 2,
                onRender,
                mapSamples: 10000, // Reduced from 16000
            };

            globeRef.current = createGlobe(canvasRef.current, optimizedConfig);
            setTimeout(() => {
                if (canvasRef.current) canvasRef.current.style.opacity = '1';
            }, 100);
        }

        return () => {
            window.removeEventListener('resize', onResize);
            if (globeRef.current) {
                globeRef.current.destroy();
                globeRef.current = null;
            }
        };
    }, [mounted, onRender, isVisible, onResize, isDark]);

    return (
        <canvas
            ref={canvasRef}
            className="w-[250px] h-[250px] xs:w-[300px] xs:h-[300px] sm:w-[400px] sm:h-[400px] md:w-[400px] md:h-[400px] lg:w-[700px] lg:h-[700px] pointer-events-none opacity-100 transition-opacity duration-1000 absolute sm:top-[-56px] md:top-[-40px] lg:top-[-66px] xl:top-[-60px] -right-10 xs:right-[-30px] sm:right-[-90px] md:right-[-100px] lg:right-[-150px] z-0"
            style={{
                contain: 'layout style size',
            }}
        />
    );
}

const FooterContactInfo = () => {
    return (
        <div className="relative py-6 xs:py-8 sm:py-12 md:py-16 get-touch-background">
            {/* Globe Background */}

            {/* Content */}
            <div className="container relative z-40 px-4 mx-auto sm:px-6">
                <div className="flex overflow-hidden absolute inset-0 md:top-[-40px] lg:top-[-66px] xl:top-[-60px] lg:right-[-100px] justify-end items-start pt-0 pr-0 lg:overflow-visible sm:pt-6 md:pt-4 sm:pr-4 md:pr-8 lg:pr-12">
                    <div className="relative opacity-40">
                        <div className="max-w-full 2xl:max-w-[1500px] 2xl:mx-auto 2xl:relative dark:opacity-70">
                            <Globe />
                        </div>
                    </div>
                </div>
                <div className="grid grid-cols-1 gap-y-6 sm:gap-y-8 text-secondary dark:text-light md:grid-cols-12 lg:grid-cols-12 z-[100] relative">
                    <div className="col-span-1 md:col-span-6 lg:col-span-3">
                        <div
                            data-aos="fade-up"
                            data-aos-duration={1000}
                            data-aos-delay={100}
                            className=" transition-all duration-300"
                        >
                            <p className="mb-2 font-bold footer-title text-lg text-textcolor dark:text-light">Certifications</p>
                            <hr className="opacity-10 bg-secondary mb-4 dark:bg-[#3C4561] dark:opacity-100" />

                            <div className="flex flex-wrap items-center gap-2.5">
                                <Image
                                    src="/img/ISO.svg"
                                    width={120}
                                    height={120}
                                    alt="ISO Certification"
                                    title="ISO Certification"
                                    className="block dark:hidden"
                                    loading="lazy"
                                />
                                <Image
                                    src="/img/ic-iso-white.svg"
                                    width={120}
                                    height={120}
                                    alt="ISO Certification"
                                    title="ISO Certification"
                                    className="hidden dark:block"
                                    loading="lazy"
                                />

                                <Image
                                    src="/img/CMMI.svg"
                                    width={120}
                                    height={120}
                                    alt="CMMI Level 3"
                                    title="CMMI Level 3"
                                    className="block dark:hidden"
                                    loading="lazy"
                                />
                                <Image
                                    src="/img/ic-cmmi-white.svg"
                                    width={120}
                                    height={120}
                                    alt="CMMI Level 3"
                                    title="CMMI Level 3"
                                    className="hidden dark:block"
                                    loading="lazy"
                                />
                            </div>
                        </div>
                    </div>
                    {/* Get In Touch Section */}
                    <div className="col-span-1 md:col-span-6 lg:col-span-3">
                        <div
                            data-aos="fade-up"
                            data-aos-duration={1000}
                            data-aos-delay={100}
                            className=" transition-all duration-300"
                        >
                            <p className="mb-2 font-bold footer-title text-lg text-textcolor dark:text-light">Get In Touch</p>
                            <hr className="opacity-10 bg-secondary mb-4 dark:bg-[#3C4561] dark:opacity-100" />

                            <div className="space-y-3">
                                <p className="flex items-center mb-3 font-medium cursor-pointer link-footer transition-all hover:translate-x-1 duration-300">
                                    <span
                                        className="flex items-center cursor-pointer link-footer"
                                        onClick={() => {
                                            window.open('https://api.whatsapp.com/send/?phone=918469699995&text=Hi');
                                        }}
                                    >
                                        <MWSVG id="ic-hr" svgClassName="me-2 flex-shrink-0 block dark:hidden" />
                                        <MWSVG id="ic-hr-black" svgClassName="me-2 flex-shrink-0 hidden dark:block" />
                                        <span className="text-sm sm:text-base">+91 846 969 9995</span>
                                    </span>
                                </p>
                                <p className="mb-3 font-medium">
                                    <span
                                        className="flex items-center cursor-pointer link-footer transition-all hover:translate-x-1 duration-300"
                                        onClick={() => {
                                            window.open('mailto:sales@moweb.com');
                                        }}
                                    >
                                        <MWSVG id="ic-email" svgClassName="me-2 flex-shrink-0" />
                                        <span className="text-sm sm:text-base">sales@moweb.com</span>
                                    </span>
                                </p>
                                <p className="font-medium">
                                    <span
                                        className="flex items-center cursor-pointer link-footer transition-all hover:translate-x-1 duration-300"
                                        onClick={() => {
                                            window.open('https://api.whatsapp.com/send/?phone=+91 8123456521&text=Hi');
                                        }}
                                    >
                                        <MWSVG id="ic-call" svgClassName="me-2 flex-shrink-0" />
                                        <span className="text-sm sm:text-base">+91 812 345 6521</span>
                                    </span>
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Locations Section */}
                    <div className="col-span-1 mt-6 md:col-span-12 lg:col-span-6 md:mt-0">
                        <div className=" transition-all duration-300">
                            <div className="mb-4">
                                <p className="font-bold footer-title text-lg text-textcolor dark:text-light mb-2">
                                    Our Locations
                                </p>
                                <hr className="opacity-10 bg-secondary dark:bg-[#3C4561] dark:opacity-100" />
                            </div>

                            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-2">
                                {/* India Card */}
                                <div
                                    className="flex flex-col rounded-lg transition-all duration-300 gap-2"
                                    data-aos="fade-up"
                                    data-aos-duration={1000}
                                    data-aos-delay={150}
                                >
                                    <p className="footer-title mb-3 text-textcolor dark:text-light flex items-center">
                                        <Image
                                            src="/img/img-india.webp"
                                            width={40}
                                            height={23}
                                            alt="flag india"
                                            title="flag india"
                                            className="me-2.5"
                                        />
                                        <span className="text-base sm:text-lg">India</span>
                                    </p>
                                    <p className="mb-0 font-medium text-sm sm:text-base">
                                        Sarthak House, Swastik Cross Road,
                                        <br /> C. G. Road, Ahmedabad - 380009
                                    </p>
                                    <p className="mb-0 font-medium text-sm sm:text-base">
                                        Sales:{' '}
                                        <span
                                            className="cursor-pointer link-footer hover:underline"
                                            onClick={() => {
                                                window.open('https://call.whatsapp.com/voice/Bnd10GyfYzarIbQeUHMYHE');
                                            }}
                                        >
                                            +91 971 299 2717
                                        </span>
                                    </p>
                                </div>

                                {/* USA Card */}
                                <div
                                    className="flex flex-col rounded-lg transition-all duration-300 gap-2"
                                    data-aos="fade-up"
                                    data-aos-duration={1000}
                                    data-aos-delay={200}
                                >
                                    <p className="footer-title mb-0 text-textcolor dark:text-light flex items-center">
                                        <Image
                                            src="/img/img-usa.webp"
                                            width={40}
                                            height={23}
                                            alt="flag usa"
                                            title="flag usa"
                                            className="me-2.5"
                                        />
                                        <span className="text-base sm:text-lg">USA</span>
                                    </p>
                                    <p className="mb-0 font-medium text-sm sm:text-base">
                                        11 Blanche St, Secaucus, New Jersey (NJ) 07094
                                    </p>
                                    {/*<p className="mb-0 font-medium">*/}
                                    {/*  Sales:{" "}*/}
                                    {/*  <Link href="tel:+1 (862) 223-8944">+1 (862) 223-8944</Link>*/}
                                    {/*</p>*/}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default FooterContactInfo;
